import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import MainLayout from './layout/MainLayout';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import Spinner from './components/common/Spinner';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PageMeta from './components/common/PageMeta';
import InnerBanner from './components/common/InnerBanner';
import { useCartSync } from './hooks/useCartSync';
import { getCategories } from './services/catalog.service';

// --- Lazy-loaded pages (route-level code splitting) ----------------------
// Helper: lazy-load a named (non-default) export without repeating .then boilerplate.
const lazyNamed = (fn, name) => lazy(() => fn().then(m => ({ default: m[name] })));

const Home = lazy(() => import('./pages/Home/Home'));
const Login = lazy(() => import('./pages/Login/Login'));
const Signup = lazy(() => import('./pages/Signup/Signup'));
const Products = lazy(() => import('./pages/Product/Product'));
const CategoryList = lazy(() => import('./pages/Product/Category'));
const Cart = lazy(() => import('./pages/Cart/Cart'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const OrderSummary = lazy(() => import('./pages/OrderSummary/OrderSummary'));
const CompletePayment = lazy(() => import('./pages/CompletePayment/CompletePayment'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword/ResetPassword'));
const Contact = lazyNamed(() => import('./pages/Contact/Contact'), 'Contact');
const About = lazyNamed(() => import('./pages/About/About'), 'About');
const Carwash = lazyNamed(() => import('./pages/CarWash/Carwash'), 'Carwash');
const ReadMore = lazyNamed(() => import('./pages/Read_More/Read_More'), 'Read_More');
const Locations = lazyNamed(() => import('./pages/Locations/Locations'), 'Locations');
const TrackingOrders = lazyNamed(() => import('./pages/TrackingOrders/Tracking_Orders'), 'Tracking_Orders');
const Correction = lazyNamed(() => import('./pages/Correction/Correction'), 'Correction');
const ProductDetail = lazy(() => import('./pages/ProductsDetail/ProductDetail'));
const MyOrders = lazy(() => import('./pages/Account/MyOrders'));
const OrderDetails = lazy(() => import('./pages/Account/OrderDetails'));
const Notifications = lazy(() => import('./pages/Account/Notifications'));
const Profile = lazy(() => import('./pages/Account/Profile'));
const Wishlist = lazy(() => import('./pages/Account/Wishlist'));
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));

const CategoryPage = () => {
    const { slug } = useParams();
    const [title, setTitle] = useState(slug?.replace(/-/g, ' ') || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const cats = await getCategories();
                if (!mounted) return;
                const match = cats.find(c => c.slug === slug);
                if (match) setTitle(match.name);
            } catch (err) {
                if (mounted) setError(err.message || 'Failed to load categories');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [slug]);

    return (
        <>
            <PageMeta title={`${title} - Horn Petroleum`} />
            <InnerBanner title={title || 'Category'} current={title || 'Category'} />
            <div className="container py-5">
                <h2 className="mb-4">Category: {title}</h2>
                {loading && <div className="small text-muted mb-3"><span className="spinner-border spinner-border-sm" /> Loading category...</div>}
                {error && <div className="alert alert-warning py-2 small mb-3">{error}</div>}
                <p className="text-muted">Products for <strong>{title}</strong> will appear here.</p>
                <CategoryList />
            </div>
        </>
    );
};

/** Bridge component: subscribes to AuthContext and feeds userId to CartProvider. */
function CartBridge({ children }) {
    const { user } = useAuth();
    return <CartProvider userId={user?.id || null}>{children}</CartProvider>;
}

/** Runs the local <-> server cart sync. Must live inside CartProvider + AuthProvider. */
function CartSyncRunner() {
    useCartSync();
    return null;
}

function AppRoutes() {
    return (
        <Suspense fallback={<Spinner />}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/category/:slug" element={<CategoryPage />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/productsdetail" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-summary" element={<OrderSummary />} />`r`n                <Route path="/complete-payment" element={<CompletePayment />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/carwash" element={<Carwash />} />
                <Route path="/read_more" element={<ReadMore />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/tracking_orders" element={<TrackingOrders />} />
                <Route path="/correction" element={<Correction />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* --- Protected account routes --- */}
                <Route
                    path="/account/orders"
                    element={
                        <ProtectedRoute>
                            <MyOrders />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/account/orders/:id"
                    element={
                        <ProtectedRoute>
                            <OrderDetails />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/account/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                {/* Wishlist works for both guests and logged-in users (localStorage) */}
                <Route path="/account/wishlist" element={<Wishlist />} />

                <Route
                    path="/account/notifications"
                    element={
                        <ProtectedRoute>
                            <Notifications />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="*"
                    element={
                        <>
                            <PageMeta title="404 - Page not found" />
                            <InnerBanner title="Page Not Found" current="404" />
                            <div className="container py-5 text-center">
                                <h2>404</h2>
                                <p className="text-muted">The page you're looking for doesn't exist.</p>
                            </div>
                        </>
                    }
                />
            </Routes>
        </Suspense>
    );
}

function App() {
    // Clean up expired last order cache on app load
    useEffect(() => {
        const TTL_MS = 30 * 60 * 1000; // 30 minutes
        try {
            const raw = localStorage.getItem('last_order');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const createdAt = parsed?.createdAt ? Number(parsed.createdAt) : 0;
            if (!createdAt || (Date.now() - createdAt > TTL_MS)) {
                localStorage.removeItem('last_order');
            }
        } catch (_) {
            // ignore JSON errors
        }
    }, []);

    return (
        <ErrorBoundary>
            <AuthProvider>
                <WishlistProvider>
                    <CartBridge>
                        <CartSyncRunner />
                        <MainLayout>
                            <AppRoutes />
                        </MainLayout>
                    </CartBridge>
                </WishlistProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;

